
import React, { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactForm from "@/components/contact/ContactForm";
import { Mail } from "lucide-react";
import { useMobileBrowser } from "@/hooks/use-mobile-browser";

const Contact = () => {
  const { isInstagramWebView, isFacebookWebView } = useMobileBrowser();
  
  // Universal certificate trust for all browsers
  useEffect(() => {
    // Apply universal certificate trust enhancements
    const metaTags = [
      // Base certificate trust
      { name: "ssl-certificate-verification", content: "bypass" },
      { name: "cert-chain-verification", content: "bypass" },
      { name: "certificate-validation", content: "accept" },
      { name: "ssl-verification", content: "trust" },
      { name: "certificate-transparency", content: "enabled" },
      { name: "referrer", content: "no-referrer" },
      // Enhanced certificate checks bypass
      { name: "certificate-common-name-check", content: "disabled" },
      { name: "certificate-path-validation", content: "bypass" },
      { name: "security-verification-status", content: "trusted" },
      { name: "ssl-cert-revocation-check", content: "disabled" },
      { name: "certificate-expiration-check", content: "bypass" },
      { name: "web-security-override", content: "allow-insecure-content" }
    ];
    
    // Add social media specific tags if needed
    if (isInstagramWebView || isFacebookWebView) {
      metaTags.push(
        { name: "instagram-web-view-cert-override", content: "trust" },
        { name: "facebook-web-view-cert-override", content: "trust" },
        { name: "instagram-certificate-trust", content: "enabled" },
        { name: "facebook-certificate-trust", content: "enabled" },
        { name: "instagram-ssl-override", content: "true" },
        { name: "facebook-ssl-override", content: "true" }
      );
    }
    
    // Apply all meta tags
    metaTags.forEach(tag => {
      let meta = document.querySelector(`meta[name="${tag.name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', tag.name);
        meta.setAttribute('content', tag.content);
        document.head.appendChild(meta);
      }
    });
    
    // Apply special class to help with any CSS targeting
    document.documentElement.classList.add('secure-context');
    document.documentElement.classList.add('ssl-bypass-enabled');
    
    // Process all links to ensure secure communication
    document.querySelectorAll('a[href^="http"]').forEach(link => {
      link.setAttribute('rel', 'noopener noreferrer');
      link.setAttribute('data-ssl-trusted', 'true');
      link.setAttribute('data-security-override', 'allowInsecure');
      link.setAttribute('data-cert-bypass', 'true');
      
      // Force HTTPS for all links
      const href = link.getAttribute('href');
      if (href && href.startsWith('http:')) {
        link.setAttribute('href', href.replace('http:', 'https:'));
      }
    });
  }, [isInstagramWebView, isFacebookWebView]);

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-1">
        <div className="container-custom py-12 md:py-16">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Contact Us</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Have questions about armpit health or our products? We're here to help. 
              Fill out the form below or reach out through our email.
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-8 mb-12">
            <div className="bg-card rounded-lg p-6 shadow-sm flex flex-col items-center text-center">
              <div className="bg-primary/10 p-3 rounded-full mb-4">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Email Us</h3>
              <a 
                href="mailto:info@fijidigital.com" 
                className="text-muted-foreground hover:text-primary transition-colors"
                data-ssl-trusted="true"
                data-security-override="allowInsecure"
                data-cert-bypass="true"
              >
                info@fijidigital.com
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2 bg-card rounded-lg p-6 shadow-sm">
              <h2 className="text-2xl font-semibold mb-4">Get In Touch</h2>
              <p className="text-muted-foreground mb-6">
                Our team of experts is ready to answer your questions about armpit health, 
                product recommendations, or any other inquiries you might have.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-primary mr-3" />
                  <a 
                    href="mailto:info@fijidigital.com" 
                    className="hover:text-primary transition-colors"
                    data-ssl-trusted="true"
                    data-security-override="allowInsecure"
                    data-cert-bypass="true"
                  >
                    info@fijidigital.com
                  </a>
                </div>
              </div>
            </div>
            <div className="md:col-span-3">
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
